import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import { userAtom } from 'src/app/state/user';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';

const scriptId = 'decagon-script';

// Constants for chat widget dimensions
const CHAT_WIDGET_DEFAULT_WIDTH = '400px';
const CHAT_WIDGET_DEFAULT_HEIGHT = '600px';
const CHAT_WIDGET_HIDDEN_SIZE = '0px';

/**
 * Helper function to find and return the Decagon chat widget container
 * Tries multiple selectors to find the widget (iframe or container)
 */
const findChatWidgetContainer = (): HTMLElement | null => {
  // Prioritize the iframe itself over its parent or other containers
  const decagonContainer = document.querySelector('iframe[src*="decagon.ai"]');

  return decagonContainer instanceof HTMLElement ? decagonContainer : null;
};

// more here if necessary https://docs.decagon.ai/adding-decagon-to-your-website/overview
export type DecagonApi = {
  close: () => void;
  hide: () => void;
  open: () => void;
  setMetadata: (metadata: Record<string, any>) => void;
  setUserId: (userId: string) => void;
};

export function useDecagon() {
  const [ready, setReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const user = useRecoilValue(userAtom);
  const { locations, selectedLocation, companies, selectedCompany } = useRecoilValue(userDispensariesAtom);
  const activeLocation = (locations || [])?.find((loc) => loc.LocId === selectedLocation.LocId);
  const activeCompany = (companies || [])?.find((company) => company.LspId === selectedCompany.LspId);
  const logrocketChatWidgetFixRollout = useLDFlag(LD.LOGROCKET_CHAT_WIDGET_FIX_ROLLOUT, false);

  // Function to resize chat widget for LogRocket integration
  const resizeChatWidget = useCallback(
    (width: string, height: string) => {
      if (logrocketChatWidgetFixRollout) {
        try {
          const decagonContainer = findChatWidgetContainer();

          if (decagonContainer) {
            decagonContainer.style.width = width;
            decagonContainer.style.height = height;
            decagonContainer.style.overflow = width === CHAT_WIDGET_HIDDEN_SIZE ? 'hidden' : 'visible';
          }
        } catch (error) {
          console.warn('LogRocket: Error resizing Decagon chat widget:', error);
        }
      }
    },
    [logrocketChatWidgetFixRollout]
  );

  // Listen for postMessage events from Decagon iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check if message is from Decagon's domain
      if (event.origin.includes('decagon.ai')) {
        const { data } = event;
        if (typeof data === 'object' && data !== null) {
          if (data.type === 'DECAGON_INACTIVE' && data.isHidden === true) {
            setIsOpen(false);
            resizeChatWidget(CHAT_WIDGET_HIDDEN_SIZE, CHAT_WIDGET_HIDDEN_SIZE);
          } else if (data.type === 'DECAGON_ACTIVE' && data.isHidden === false) {
            setIsOpen(true);
            resizeChatWidget(CHAT_WIDGET_DEFAULT_WIDTH, CHAT_WIDGET_DEFAULT_HEIGHT);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [resizeChatWidget]);

  const decagon: DecagonApi | null = useMemo(() => {
    if (!ready || !window.duet) {
      return null;
    }

    return {
      hide: () => {
        window.duet.hide();
        setIsOpen(false); // Manual state tracking
        resizeChatWidget(CHAT_WIDGET_HIDDEN_SIZE, CHAT_WIDGET_HIDDEN_SIZE);
      },
      close: () => {
        window.duet.close();
        setIsOpen(false); // Manual state tracking
        resizeChatWidget(CHAT_WIDGET_HIDDEN_SIZE, CHAT_WIDGET_HIDDEN_SIZE);
      },
      open: () => {
        setIsOpen(true); // Manual state tracking
        // Restore normal size when opening
        resizeChatWidget(CHAT_WIDGET_DEFAULT_WIDTH, CHAT_WIDGET_DEFAULT_HEIGHT);
        window.duet.open();
      },
      setMetadata: (metadata) => window.duet.setMetadata(metadata),
      setUserId: (userId) => window.duet.setUserId(userId),
    };
  }, [ready, resizeChatWidget]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://decagon.ai/loaders/dutchie.js';
    script.async = true;
    script.id = scriptId;
    script.addEventListener('load', () => {
      setReady(true);
      window.duet.hide();

      if (logrocketChatWidgetFixRollout) {
        setTimeout(() => {
          try {
            const decagonContainer = findChatWidgetContainer();
            if (decagonContainer) {
              decagonContainer.style.width = CHAT_WIDGET_HIDDEN_SIZE;
              decagonContainer.style.height = CHAT_WIDGET_HIDDEN_SIZE;
              decagonContainer.style.overflow = 'hidden';
              decagonContainer.setAttribute('data-logrocket-hidden', 'true');
            }
          } catch (error) {
            console.warn('LogRocket: Error resizing Decagon chat widget (flag was true):', error);
          }
        }, 100);
      }
    });

    if (!window.duet) {
      document.body.appendChild(script);
    }

    return () => {
      if (document.getElementById(scriptId)) {
        document.body.removeChild(script);
      }
    };
  }, [logrocketChatWidgetFixRollout]);

  useEffect(() => {
    if (decagon) {
      decagon.setUserId(String(user.Id));
      decagon.setMetadata({
        userId: String(user.Id),
        location: activeLocation?.Name,
        company: activeCompany?.LspName,
        name: user.FullName,
        email: user.Email || user.UserName,
      });
    }
  }, [decagon, user, activeLocation, activeCompany]);

  return { decagon, isOpen };
}
